"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, Scale, Stethoscope, Wrench, Zap, ShoppingBag, Briefcase, Building2, ArrowRight, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectorsData = [{
  id: "accountants",
  title: "Accountants & Advisory",
  description: "Target high-value commercial clients searching for local tax, audit, and wealth management expertise.",
  icon: Calculator,
  badge: "Finance"
}, {
  id: "solicitors",
  title: "Solicitors & Law Firms",
  description: "Dominate search engine results for specialized practice areas and capture intent-driven legal leads.",
  icon: Scale,
  badge: "Legal"
}, {
  id: "dentists",
  title: "Dentists & Clinics",
  description: "Attract private patients seeking high-margin cosmetic and restorative treatments in your area.",
  icon: Stethoscope,
  badge: "Healthcare"
}, {
  id: "plumbers",
  title: "Plumbers & Heating",
  description: "Secure immediate call-out traffic and high-value boiler installation queries across your service areas.",
  icon: Wrench,
  badge: "Trades"
}, {
  id: "electricians",
  title: "Electricians & Contractors",
  description: "Gain organic visibility for commercial contracts, EV charger fits, and domestic electrical work.",
  icon: Zap,
  badge: "Trades"
}, {
  id: "ecommerce",
  title: "Ecommerce Stores",
  description: "Scale organic revenue through optimized category structures, product schemas, and search visibility.",
  icon: ShoppingBag,
  badge: "Retail"
}, {
  id: "b2btech",
  title: "B2B Tech & SaaS",
  description: "Drive qualified demo requests and trial signups with decision-stage content and technical optimization.",
  icon: Briefcase,
  badge: "Technology"
}, {
  id: "estateagents",
  title: "Estate Agents & Property",
  description: "Outrank regional competitors and property portals to capture valuation requests from local vendors.",
  icon: Building2,
  badge: "Property"
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Sectors() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="industrysectorsgrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            Sectors I Serve
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold font-default tracking-tight text-foreground mb-4">
            Tailored SEO Strategies Built for Your Industry
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Every market has unique search behaviors and conversion paths. I design bespoke search strategies 
            tailored to your exact business model—delivering sustainable, organic growth without generic templates.
          </p>
        </div>

        <motion.div variants={containerVariants} initial={shouldReduceMotion ? "visible" : "hidden"} whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sectorsData.map((sector, i) => {
          const IconComponent = sector.icon;
          return <motion.div key={sector.id} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full bg-card border border-border/80 hover:border-primary/50 transition-[border-color,box-shadow] duration-300 hover:shadow-md flex flex-col justify-between">
                  <CardHeader className="p-6 pb-2">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <Badge variant="outline" className="text-xs text-muted-foreground border-border">
                        {sector.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold font-default text-foreground">
                      {sector.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-2 flex-1 flex flex-col justify-between">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mb-4">
                      {sector.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-12 md:mt-16 bg-muted/50 border border-border rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start md:items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-primary shrink-0 mt-1 md:mt-0">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold font-default text-foreground">
                Don&apos;t see your specific niche listed?
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                I work directly with specialized local, B2B, and international businesses across many additional sectors.
              </p>
            </div>
          </div>
          <div className="shrink-0 w-full md:w-auto">
            <Button size="lg" className="w-full md:w-auto gap-2" asChild>
              <Link to="/ContactPage">
                <span>Book a Free Consultation</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}